import React, { useEffect, useState } from 'react';
import QRCode from 'qrcode';

interface QrCodeProps {
  /** The data encoded in the QR code (e.g. a barcode or wallet token). */
  value: string;
  /** Rendered image size in pixels. Defaults to 256. */
  size?: number;
  /** Extra classes for layout/spacing. A white background is always applied. */
  className?: string;
}

/**
 * QR code that always renders as black modules on a white background, even when
 * the browser or OS forces a dark theme.
 *
 * Android "force dark" implementations (Chrome's Auto Dark Theme, Samsung
 * Internet, Xiaomi/MIUI, WebView `force-dark`) lightness-invert page content,
 * which turns a QR code into a near-white code on a dark background that
 * scanners can no longer read. Crucially, these engines skip real images
 * (`<img>`, `<picture>`, backgrounds, video) but treat an inline `<svg>` as
 * page content and invert it — and the CSS `color-scheme: only light` opt-out
 * is honoured inconsistently across those engines.
 *
 * Rendering the QR as a raster PNG in an `<img>` sidesteps this entirely: the
 * inversion heuristics leave images alone, so the code stays black-on-white on
 * every device. (System-wide accessibility colour inversion still inverts
 * everything and cannot be overridden from web content — but that is a
 * deliberate accessibility toggle, not the automatic browser darkening this
 * guards against.)
 *
 * @see https://developer.chrome.com/blog/auto-dark-theme
 */
const QrCode: React.FC<QrCodeProps> = ({ value, size = 256, className }) => {
  const [dataUrl, setDataUrl] = useState<string>();

  useEffect(() => {
    let cancelled = false;
    QRCode.toDataURL(value, {
      errorCorrectionLevel: 'H',
      margin: 1,
      width: size,
      color: { dark: '#000000', light: '#FFFFFF' },
    })
      .then((url) => {
        if (!cancelled) setDataUrl(url);
      })
      .catch(() => {
        if (!cancelled) setDataUrl(undefined);
      });
    return () => {
      cancelled = true;
    };
  }, [value, size]);

  if (!dataUrl) return null;

  return (
    <img
      src={dataUrl}
      alt="QR code"
      width={size}
      className={className}
      style={{ height: 'auto', colorScheme: 'only light' }}
    />
  );
};

export default QrCode;
